/**
 * Receipts on screen (v9.0.0) — the words and the small decisions, kept free of
 * the DOM so the unit suite can test them.
 */

import type { ApiReceipt, ApiReceiptType, ApiTransaction } from './api';
import { formatMoneyExact } from './envelope-math';

/** The client's own copy of the server's list. A type not on it is never turned into a Blob. */
export const RECEIPT_TYPES: readonly ApiReceiptType[] = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'application/pdf',
];

/** What the file picker offers. Every image, so the iPhone shows Photos and the camera. */
export const RECEIPT_ACCEPT = 'image/*,application/pdf';

const EXTENSION: Record<ApiReceiptType, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/heic': 'heic',
  'application/pdf': 'pdf',
};

export const isPhoto = (type: ApiReceiptType) => type !== 'application/pdf';

/** The name a saved receipt gets — the same pattern the server sends. Never an uploaded name. */
export function receiptFilename(r: ApiReceipt): string {
  const day = new Date(r.createdAt * 1000).toISOString().slice(0, 10);
  return `receipt-${day}-${r.id.slice(0, 8)}.${EXTENSION[r.contentType]}`;
}

export function sizeText(bytes: number): string {
  if (bytes < 1024) return `${bytes} bytes`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const DAY = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });

/** "Oct 1" for a yyyy-mm-dd date (UTC, so it is the bank's date and never shifts a day). */
export function transactionDateText(date: string): string {
  return DAY.format(new Date(`${date}T00:00:00Z`));
}

/** "Photo · 1.2 MB · added Oct 10" */
export function receiptLine(r: ApiReceipt): string {
  const added = DAY.format(new Date(r.createdAt * 1000));
  return `${isPhoto(r.contentType) ? 'Photo' : 'PDF'} · ${sizeText(r.sizeBytes)} · added ${added}`;
}

export function receiptCountText(n: number): string | null {
  return n === 0 ? null : n === 1 ? '1 receipt' : `${n} receipts`;
}

/** Spending reads as spending: "−$42.10". Money in has no sign. */
export function transactionAmountText(t: Pick<ApiTransaction, 'amountMinor'>): string {
  return t.amountMinor < 0
    ? `−${formatMoneyExact(-t.amountMinor)}`
    : formatMoneyExact(t.amountMinor);
}

export function transactionMeta(t: ApiTransaction): string {
  return [
    transactionDateText(t.date),
    t.account,
    t.pending ? 'pending' : null,
    t.removed ? 'reversed by the bank' : null,
    receiptCountText(t.receipts.length),
  ]
    .filter((part): part is string => part != null)
    .join(' · ');
}

/** Whether another receipt may be added — the button is drawn only when it can work. */
export function canAddReceipt(
  t: ApiTransaction,
  opts: { receiptsEnabled: boolean; perTransaction: number },
): boolean {
  return opts.receiptsEnabled && !t.removed && t.receipts.length < opts.perTransaction;
}

/**
 * The size a photo is re-drawn at before upload. Never larger than it is; the
 * longest side at most 4096 px, which keeps the canvas under iOS Safari's
 * canvas-area ceiling and is far more than a receipt needs to be read.
 */
export function fitWithin(
  width: number,
  height: number,
  maxSide = 4096,
): { width: number; height: number } {
  const scale = Math.min(1, maxSide / Math.max(width, height));
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/** Before anything is sent: a file the server would refuse for size alone. */
export function tooLargeText(bytes: number, maxBytes: number): string | null {
  return bytes > maxBytes
    ? `That file is ${sizeText(bytes)}; the limit is ${sizeText(maxBytes)}. Nothing was saved.`
    : null;
}
